import type { ViewQueryRecordNode } from '@salescore/core'
import { isNotNullish, isNotUndefined, isUndefined } from 'effect/Predicate'

import type { RSheetColumn, RSheetRecordNode } from '../../column'
import type { RSheetsCursor, RSheetsCursorPosition } from '../cursorTypes'

export type EdgeType = 'left' | 'right' | 'up' | 'down'

export const TOP_LEFT_CURSOR_POSITION = { rowIndex: 0, columnIndex: 1, innerRowIndex: 0 }

const defaultPosition: RSheetsCursorPosition = {
  rowIndex: 0,
  columnIndex: 0,
  innerRowIndex: 0,
}

const maxInnerRowIndex = (record: ViewQueryRecordNode | undefined): number => {
  if (record === undefined) {
    return 0
  }
  const childInnerStart = record.children
    .map((childNode) => maxInnerRowIndex(childNode.children.at(-1)))
    .filter(isNotNullish)
  return Math.max(...[record.meta.innerRowIndexStart, ...childInnerStart].filter(isNotNullish))
}

const resolveTargetPosition = (cursor: RSheetsCursor | undefined, expand: boolean): RSheetsCursorPosition => {
  if (isUndefined(cursor)) {
    return defaultPosition
  }
  if (expand && isNotUndefined(cursor.expand)) {
    return cursor.expand
  }
  return cursor.main
}

const moveToEdge = (
  target: RSheetsCursorPosition,
  edgeType: EdgeType,
  columns: Array<RSheetColumn<RSheetRecordNode>>,
  rows: ViewQueryRecordNode[],
): RSheetsCursorPosition => {
  switch (edgeType) {
    case 'left': {
      return {
        ...target,
        columnIndex: TOP_LEFT_CURSOR_POSITION.columnIndex,
      }
    }
    case 'right': {
      return {
        ...target,
        columnIndex: columns.length - 1,
      }
    }
    case 'up': {
      return {
        ...target,
        innerRowIndex: TOP_LEFT_CURSOR_POSITION.innerRowIndex,
        rowIndex: TOP_LEFT_CURSOR_POSITION.rowIndex,
      }
    }
    case 'down': {
      return {
        ...target,
        innerRowIndex: maxInnerRowIndex(rows.at(-1)),
        rowIndex: rows.length - 1,
      }
    }
  }
}

export const getNextCursor = ({
  cursor,
  columns,
  rows,
  type,
  expand,
}: {
  cursor: RSheetsCursor | undefined
  columns: Array<RSheetColumn<RSheetRecordNode>>
  rows: ViewQueryRecordNode[]
  type: EdgeType
  expand: boolean
}): RSheetsCursor => {
  const target = resolveTargetPosition(cursor, expand)
  const moved = moveToEdge(target, type, columns, rows)

  const base: RSheetsCursor = cursor ?? { main: defaultPosition }
  const _expand = expand ? { expand: moved } : { main: moved, expand: undefined }
  return { ...base, ..._expand }
}
